import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Edit, Delete, Add, DirectionsBus, Warning } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { 
  checkPlacasExist, 
  getVehiclesByTransportLine, 
  createVehicle, 
  updateVehicle, 
  deleteVehicle 
} from '../../api';
import ConfirmDialog from '../ConfirmDialog';

const VehiclesManagement = ({ selectedLine, onDataChange }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    placas: '',
    tipo: '',
    modelo: '',
    año: '',
    color: '',
    permisoSCT: '',
    estado: 'ACTIVO'
  });
  const [placasError, setPlacasError] = useState("");
  const [isValidatingPlacas, setIsValidatingPlacas] = useState(false);

  const vehicleTypes = [
    "1.5 TONS",
    "3.5 TONS",
    "RABÓN",
    "TORTON",
    "TRACTO"
  ];

  useEffect(() => {
    if (selectedLine) {
      fetchVehicles();
    }
  }, [selectedLine]);

  // Debounce function to prevent too many API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Función para verificar si un vehículo tiene datos incompletos
  const isVehicleIncomplete = (vehicle) => {
    return !vehicle.placas || !vehicle.tipo || !vehicle.modelo || !vehicle.año || !vehicle.color || !vehicle.permisoSCT;
  };
  
  // Función para obtener los campos faltantes específicos de un vehículo
  const getMissingFields = (vehicle) => {
    const missing = [];
    if (!vehicle.placas) missing.push('Placas');
    if (!vehicle.tipo) missing.push('Tipo');
    if (!vehicle.modelo) missing.push('Modelo');
    if (!vehicle.año) missing.push('Año');
    if (!vehicle.color) missing.push('Color');
    if (!vehicle.permisoSCT) missing.push('Permiso SCT');
    return missing;
  };

  const validatePlacas = async (placas) => {
    if (!placas || placas.trim().length < 3) return;
    
    setIsValidatingPlacas(true);
    try {
      const response = await checkPlacasExist(placas.trim());
      if (response.exists) {
        // If we're editing an existing vehicle and it's the same one, don't show an error
        if (editingVehicle && response.vehicle_id === editingVehicle.id) {
          setPlacasError("");
        } else {
          setPlacasError(
            `Las placas ${placas} ya están registradas para la línea de transporte: ${response.linea_transporte.name} (${response.linea_transporte.razon_Social})`
          );
        }
      } else {
        setPlacasError("");
      }
    } catch (error) {
      console.error("Error validating plates:", error);
    } finally {
      setIsValidatingPlacas(false);
    }
  };

  // Create a debounced version of validatePlacas
  const debouncedValidatePlacas = debounce(validatePlacas, 500);

  const fetchVehicles = async () => {
    if (!selectedLine) return;
    
    setLoading(true);
    try {
      const data = await getVehiclesByTransportLine(selectedLine.id);
      setVehicles(data);

      // Notify parent component about data changes
      if (onDataChange) {
        onDataChange(selectedLine);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error al cargar los vehículos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehicle = null) => {
    setPlacasError(""); // Clear previous error
    if (vehicle) {
      setEditingVehicle(vehicle);
      setVehicleForm({
        placas: vehicle.placas || '',
        tipo: vehicle.tipo || '',
        modelo: vehicle.modelo || '',
        año: vehicle.año || '',
        color: vehicle.color || '',
        permisoSCT: vehicle.permisoSCT || '',
        estado: vehicle.estado || 'ACTIVO'
      });
    } else {
      setEditingVehicle(null);
      setVehicleForm({
        placas: '',
        tipo: '',
        modelo: '',
        año: '',
        color: '',
        permisoSCT: '',
        estado: 'ACTIVO'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVehicleForm({
      ...vehicleForm,
      [name]: value
    });
    
    // Validate placas when the field changes
    if (name === 'placas') {
      debouncedValidatePlacas(value);
    }
  };

  const handleSubmit = async () => {
    if (!selectedLine) return;
    
    // Final validation before submitting
    if (placasError) {
      toast.error("Por favor corrige los errores antes de continuar");
      return;
    }
    
    try {
      if (editingVehicle) {
        await updateVehicle(editingVehicle.id, {
          ...vehicleForm,
          linea_transporte_id: selectedLine.id
        });
        toast.success('Vehículo actualizado exitosamente');
      } else {
        await createVehicle({
          ...vehicleForm,
          linea_transporte_id: selectedLine.id
        });
        toast.success('Vehículo creado exitosamente');
      }
      
      fetchVehicles();
      handleCloseDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || `Error al ${editingVehicle ? 'actualizar' : 'crear'} el vehículo`;
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (vehicle) => {
    setVehicleToDelete(vehicle);
    setConfirmDelete(true);
  };

  const handleCloseDeleteConfirm = () => {
    setConfirmDelete(false);
    setVehicleToDelete(null);
  };

  const handleDeleteVehicle = async () => {
    if (!vehicleToDelete) return;
    
    try {
      await deleteVehicle(vehicleToDelete.id);
      toast.success('Vehículo eliminado correctamente');
      fetchVehicles();
      handleCloseDeleteConfirm();
    } catch (error) {
      toast.error('Error al eliminar el vehículo');
    }
  };

  if (!selectedLine) {
    return (
      <Typography variant="h6" align="center">
        Seleccione una línea de transporte para ver sus vehículos
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleOpenDialog()}
          disabled={selectedLine.blocked}
          startIcon={<Add />}
        >
          Nuevo Vehículo
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : vehicles.length === 0 ? (
        <Alert severity="info">
          No hay vehículos registrados para esta línea de transporte
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Placas</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Modelo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((vehicle) => {
                const incomplete = isVehicleIncomplete(vehicle);
                const missingFields = getMissingFields(vehicle);
                
                return (
                  <TableRow 
                    key={vehicle.id}
                    sx={{
                      backgroundColor: incomplete ? '#fff4e5' : 'inherit',  // Sombreado naranja claro para filas incompletas
                      '&:hover': {
                        backgroundColor: incomplete ? '#ffe0b2' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {incomplete && (
                          <Tooltip title={`Datos incompletos: ${missingFields.join(', ')}`}>
                            <Warning color="warning" sx={{ mr: 1 }} />
                          </Tooltip>
                        )}
                        {vehicle.placas || '(Sin placas)'}
                      </Box>
                    </TableCell>
                    <TableCell>{vehicle.tipo || '(Sin tipo)'}</TableCell>
                    <TableCell>{vehicle.modelo || '(Sin modelo)'}</TableCell>
                    <TableCell>{vehicle.estado}</TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => handleOpenDialog(vehicle)}
                        disabled={selectedLine.blocked}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        color="error"
                        onClick={() => handleDeleteClick(vehicle)}
                        disabled={selectedLine.blocked}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingVehicle ? 'Editar Vehículo' : 'Nuevo Vehículo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Placas"
                name="placas"
                value={vehicleForm.placas}
                onChange={handleInputChange}
                fullWidth
                error={!!placasError}
                helperText={placasError}
                InputProps={{
                  endAdornment: isValidatingPlacas ? <CircularProgress size={20} /> : null
                }}
                required
                sx={{ mt: 2 }}
              />
            </Grid>
           
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="tipo-label">Tipo</InputLabel>
                <Select
                  labelId="tipo-label"
                  name="tipo"
                  value={vehicleForm.tipo}
                  onChange={handleInputChange}
                  label="Tipo"
                  required
                >
                  {vehicleTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Modelo"
                name="modelo"
                value={vehicleForm.modelo}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Año"
                name="año"
                type="number"
                value={vehicleForm.año}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Color"
                name="color"
                value={vehicleForm.color}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Permiso SCT"
                name="permisoSCT"
                value={vehicleForm.permisoSCT}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="estado-label">Estado</InputLabel>
                <Select
                  labelId="estado-label"
                  name="estado"
                  value={vehicleForm.estado}
                  onChange={handleInputChange}
                  label="Estado"
                  required
                >
                  <MenuItem value="ACTIVO">ACTIVO</MenuItem>
                  <MenuItem value="MANTENIMIENTO">MANTENIMIENTO</MenuItem>
                  <MenuItem value="INACTIVO">INACTIVO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary"
            variant="contained"
            disabled={!!placasError || isValidatingPlacas}
          >
            {editingVehicle ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDelete}
        title="Confirmar eliminación"
        content={`¿Está seguro de que desea eliminar el vehículo con placas ${vehicleToDelete?.placas}?`}
        onConfirm={handleDeleteVehicle}
        onClose={handleCloseDeleteConfirm}
      />
    </Box>
  );
};

export default VehiclesManagement;
